package br.com.vvrefeicoes.portal.core.util.spreadsheet

import kotlinx.datetime.DateTimeUnit
import kotlinx.datetime.LocalDate
import kotlinx.datetime.plus

/**
 * Leitor mínimo de `.xlsx` (Office Open XML): abre o ZIP ([ZipReader]) e extrai a primeira
 * aba como uma grade de strings, resolvendo sharedStrings, valores inline, números e datas
 * (pelo número de série do Excel + `numFmtId` de data). Sem dependências externas.
 */
internal object XlsxReader {
    private val SHEET_PATH = Regex("xl/worksheets/sheet\\d+\\.xml")
    private val SHEET_ENTRY = Regex("<sheet\\b([^>]*?)/?>")
    private val RELATIONSHIP = Regex("<Relationship\\b([^>]*?)/?>")
    private val ATTR_NAME = Regex("\\bname=\"([^\"]*)\"")
    private val ATTR_R_ID = Regex("\\br:id=\"([^\"]*)\"")
    private val ATTR_REL_ID = Regex("\\bId=\"([^\"]*)\"")
    private val ATTR_TARGET = Regex("\\bTarget=\"([^\"]*)\"")
    private val ROW = Regex("<row\\b[^>]*>([\\s\\S]*?)</row>")
    private val CELL = Regex("<c\\b([^>]*?)(?:/>|>([\\s\\S]*?)</c>)")
    private val ATTR_R = Regex("\\br=\"([^\"]*)\"")
    private val ATTR_T = Regex("\\bt=\"([^\"]*)\"")
    private val ATTR_S = Regex("\\bs=\"([^\"]*)\"")
    private val VALUE = Regex("<v>([\\s\\S]*?)</v>")
    private val TEXT = Regex("<t[^>]*>([\\s\\S]*?)</t>")
    private val SHARED_ITEM = Regex("<si>([\\s\\S]*?)</si>")
    private val CELL_XFS = Regex("<cellXfs\\b[^>]*>([\\s\\S]*?)</cellXfs>")
    private val XF = Regex("<xf\\b([^>]*?)/?>")
    private val NUM_FMT = Regex("<numFmt\\b([^>]*?)/?>")
    private val NUM_FMT_ID = Regex("numFmtId=\"(\\d+)\"")
    private val NUM_FMT_CODE = Regex("formatCode=\"([^\"]*)\"")
    private val QUOTED_TEXT = Regex("\"[^\"]*\"")
    private val BRACKET_SECTION = Regex("\\[[^\\]]*\\]")
    private val ESCAPED_CHAR = Regex("\\\\.")
    private val EXCEL_EPOCH = LocalDate(1899, 12, 30)
    private val BUILTIN_DATE_IDS = (14..22).toSet() + (45..47).toSet()

    fun read(data: ByteArray): List<List<String>> = read(data) { true }

    /**
     * Lê uma aba específica. [sheetSelector] recebe o nome de cada aba do workbook, na ordem em que
     * aparece no arquivo; a primeira que retornar `true` é usada.
     */
    fun read(
        data: ByteArray,
        sheetSelector: (String) -> Boolean,
    ): List<List<String>> {
        val entries = ZipReader.read(data)
        val sheetPath = resolveSheetPath(entries, sheetSelector)
        val shared = entries["xl/sharedStrings.xml"]?.let { parseSharedStrings(it.decodeToString()) }.orEmpty()
        val dateStyles = entries["xl/styles.xml"]?.let { parseDateStyles(it.decodeToString()) }.orEmpty()
        return parseSheet(entries.getValue(sheetPath).decodeToString(), shared, dateStyles)
    }

    private fun resolveSheetPath(
        entries: Map<String, ByteArray>,
        sheetSelector: (String) -> Boolean,
    ): String {
        val sheets = parseSheets(entries)
        if (sheets.isNotEmpty()) {
            return sheets.firstOrNull { (name, _) -> sheetSelector(name) }?.second
                ?: throw SpreadsheetFormatException("Planilha XLSX sem aba correspondente")
        }
        if (!sheetSelector("")) {
            throw SpreadsheetFormatException("Planilha XLSX sem aba encontrada")
        }
        return entries.keys.firstOrNull { it == "xl/worksheets/sheet1.xml" }
            ?: entries.keys.firstOrNull { SHEET_PATH.matches(it) }
            ?: throw SpreadsheetFormatException("Planilha XLSX sem aba encontrada")
    }

    private fun parseSheets(entries: Map<String, ByteArray>): List<Pair<String, String>> {
        val workbook = entries["xl/workbook.xml"]?.decodeToString() ?: return emptyList()
        val relationships = entries["xl/_rels/workbook.xml.rels"]?.decodeToString() ?: return emptyList()
        val targets =
            RELATIONSHIP
                .findAll(relationships)
                .mapNotNull { relationship ->
                    val attributes = relationship.groupValues[1]
                    val id = ATTR_REL_ID.find(attributes)?.groupValues?.get(1) ?: return@mapNotNull null
                    val target = ATTR_TARGET.find(attributes)?.groupValues?.get(1) ?: return@mapNotNull null
                    val path =
                        when {
                            target.startsWith("/") -> target.removePrefix("/")
                            target.startsWith("xl/") -> target
                            else -> "xl/$target"
                        }
                    id to path
                }.toMap()
        return SHEET_ENTRY
            .findAll(workbook)
            .mapNotNull { sheet ->
                val attributes = sheet.groupValues[1]
                val name = ATTR_NAME.find(attributes)?.groupValues?.get(1) ?: return@mapNotNull null
                val relationshipId = ATTR_R_ID.find(attributes)?.groupValues?.get(1) ?: return@mapNotNull null
                val path = targets[relationshipId] ?: return@mapNotNull null
                if (!entries.containsKey(path)) return@mapNotNull null
                unescape(name) to path
            }.toList()
    }

    private fun parseSheet(
        xml: String,
        shared: List<String>,
        dateStyles: Set<Int>,
    ): List<List<String>> {
        val rows = ArrayList<List<String>>()
        ROW.findAll(xml).forEach { row ->
            val cells = ArrayList<String>()
            CELL.findAll(row.groupValues[1]).forEach { cell ->
                val attributes = cell.groupValues[1]
                val body = cell.groupValues.getOrNull(2).orEmpty()
                val column =
                    ATTR_R
                        .find(attributes)
                        ?.groupValues
                        ?.get(1)
                        ?.let(::columnIndex) ?: cells.size
                val value = cellValue(attributes, body, shared, dateStyles)
                while (cells.size <= column) cells.add("")
                cells[column] = value
            }
            rows.add(cells)
        }
        val width = rows.maxOfOrNull { it.size } ?: 0
        return rows.map { it + List(width - it.size) { "" } }
    }

    private fun cellValue(
        attributes: String,
        body: String,
        shared: List<String>,
        dateStyles: Set<Int>,
    ): String {
        val type = ATTR_T.find(attributes)?.groupValues?.get(1)
        val style =
            ATTR_S
                .find(attributes)
                ?.groupValues
                ?.get(1)
                ?.toIntOrNull()
        return when (type) {
            "s" -> {
                val index =
                    VALUE
                        .find(body)
                        ?.groupValues
                        ?.get(1)
                        ?.toIntOrNull() ?: -1
                shared.getOrNull(index).orEmpty()
            }
            "inlineStr" -> TEXT.findAll(body).joinToString("") { unescape(it.groupValues[1]) }
            "str" ->
                unescape(
                    VALUE
                        .find(body)
                        ?.groupValues
                        ?.get(1)
                        .orEmpty(),
                )
            else -> {
                val raw =
                    VALUE
                        .find(body)
                        ?.groupValues
                        ?.get(1)
                        ?.trim()
                        .orEmpty()
                if (raw.isNotEmpty() && style != null && style in dateStyles) {
                    formatDate(raw.toDoubleOrNull()) ?: unescape(raw)
                } else {
                    unescape(raw)
                }
            }
        }
    }

    private fun parseSharedStrings(xml: String): List<String> =
        SHARED_ITEM
            .findAll(xml)
            .map { item ->
                TEXT.findAll(item.groupValues[1]).joinToString("") { unescape(it.groupValues[1]) }
            }.toList()

    private fun parseDateStyles(xml: String): Set<Int> {
        val customDateIds = HashSet<Int>()
        NUM_FMT.findAll(xml).forEach { format ->
            val attributes = format.groupValues[1]
            val id =
                NUM_FMT_ID
                    .find(attributes)
                    ?.groupValues
                    ?.get(1)
                    ?.toIntOrNull()
            val code =
                NUM_FMT_CODE
                    .find(attributes)
                    ?.groupValues
                    ?.get(1)
                    ?.lowercase()
            if (id != null && code != null && isDateCode(code)) customDateIds.add(id)
        }
        val body = CELL_XFS.find(xml)?.groupValues?.get(1) ?: return emptySet()
        val dateStyles = HashSet<Int>()
        XF.findAll(body).forEachIndexed { index, format ->
            val id =
                NUM_FMT_ID
                    .find(format.groupValues[1])
                    ?.groupValues
                    ?.get(1)
                    ?.toIntOrNull()
            if (id != null && (id in BUILTIN_DATE_IDS || id in customDateIds)) dateStyles.add(index)
        }
        return dateStyles
    }

    internal fun isDateCode(code: String): Boolean {
        // Remove seções de cor/condição ([Red], [>=100]), textos entre aspas e escapes antes de
        // procurar tokens de data. Sem isso, "[Red]" era interpretado como data por causa do "d".
        val cleaned =
            code
                .lowercase()
                .replace(QUOTED_TEXT, "")
                .replace(BRACKET_SECTION, "")
                .replace(ESCAPED_CHAR, "")
        if (cleaned.contains("%")) return false
        return cleaned.any { it == 'y' || it == 'm' || it == 'd' || it == 'h' || it == 's' }
    }

    private fun formatDate(serial: Double?): String? {
        if (serial == null) return null
        val days = serial.toLong()
        if (days < 0 || days > 2_958_465) return null
        val date = EXCEL_EPOCH.plus(days.toInt(), DateTimeUnit.DAY)
        return "${pad2(date.day)}/${pad2(date.month.ordinal + 1)}/${date.year}"
    }

    private fun pad2(value: Int): String = if (value < 10) "0$value" else value.toString()

    private fun columnIndex(reference: String): Int {
        var index = 0
        var position = 0
        while (position < reference.length && reference[position].isLetter()) {
            index = index * 26 + (reference[position].uppercaseChar() - 'A' + 1)
            position++
        }
        return index - 1
    }

    private fun unescape(value: String): String =
        value
            .replace("&lt;", "<")
            .replace("&gt;", ">")
            .replace("&quot;", "\"")
            .replace("&apos;", "'")
            .replace("&#39;", "'")
            .replace("&amp;", "&")
}
